import { useI18n } from '@ecom/i18n';
import { Button } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useMySecurityQuery } from '@/features/my-account/api';
import { useActiveShop } from '@/features/shops/use-active-shop';

const KEY_PREFIX = 'souk-merchant-welcome-dismissed:';

function wasDismissed(tenantId: string): boolean {
  try {
    return window.localStorage.getItem(`${KEY_PREFIX}${tenantId}`) === '1';
  } catch {
    return false;
  }
}

/**
 * The welcome shown ONCE, at the top of the dashboard, when the account has just been created by the platform (lot P4, M1).
 *
 * It appears only on the account's FIRST sign-in - the sign-in history (`previousSignIn`, read from the account's own audit trail, not
 * guessed) has nothing before this one - and until it is closed in this browser. It says in words what state the shop is in (waiting
 * for the platform's approval, or approved), what that means for the public, and the next step. The only thing it remembers is that it
 * was closed, per shop: nothing sensitive, nothing about the account. A card that is not sure of itself (history unreadable, no shop
 * yet) stays silent.
 */
export function WelcomeCard(): ReactElement | null {
  const { t } = useI18n();
  const location = useLocation();
  const { activeMembership } = useActiveShop();
  const security = useMySecurityQuery();
  const [closedHere, setClosedHere] = useState(false);

  const tenantId = activeMembership?.tenantId ?? null;
  const firstSignIn = security.isSuccess && security.data.previousSignIn === null;
  if (tenantId === null || !firstSignIn || closedHere || wasDismissed(tenantId)) return null;

  const status = activeMembership?.tenantStatus;
  const passwordSaved =
    (location.state as { passwordSaved?: boolean } | null)?.passwordSaved === true;

  return (
    <section
      aria-label={t('merchant.welcome.title')}
      className="flex flex-col gap-2 rounded-xl border border-line bg-accentSoft p-5 text-accentInk"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">{t('merchant.welcome.title')}</h2>
          <p className="text-sm">{t('merchant.welcome.subtitle')}</p>
        </div>
        <button
          type="button"
          className="text-xs font-medium underline"
          onClick={() => {
            try {
              window.localStorage.setItem(`${KEY_PREFIX}${tenantId}`, '1');
            } catch {
              // The card simply comes back next time: harmless.
            }
            setClosedHere(true);
          }}
        >
          {t('merchant.welcome.dismiss')}
        </button>
      </div>

      {passwordSaved ? (
        <p role="status" className="text-sm font-medium">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="me-1.5 inline h-4 w-4 align-[-3px]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m5 12 4.5 4.5L19 7" />
          </svg>
          {t('merchant.welcome.passwordSaved')}
        </p>
      ) : null}

      {status === 'pending' ? (
        <p className="text-sm">{t('merchant.welcome.statusPending')}</p>
      ) : null}
      {status === 'active' ? <p className="text-sm">{t('merchant.welcome.statusActive')}</p> : null}

      <div>
        <Link to="/store">
          <Button size="sm">{t('merchant.welcome.cta')}</Button>
        </Link>
      </div>
    </section>
  );
}
